<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Switch,
} from '@collector/shared-ui'
import { Incident } from '@collector/sportsapi-client'
import { useIncidents } from '@desktop/views/Relation/composables'
import { IncidentsGroup } from '@desktop/views/Relation/Sports/configuration/incidentsGroup'
import { watchArray } from '@vueuse/core'

import {
  SameTime,
  useActiveIncidentFilters,
  useIncidentFiltersSelect,
  useIncidentFiltersToggle,
  useIncidentsGroup,
  useSameTimeIncidentsMap,
} from './composables'
import FilterTagSelect from './FilterTagSelect.vue'
import FilterTagToggle from './FilterTagToggle.vue'
import IncidentFiltersSelect from './IncidentFiltersSelect.vue'
import {
  filterIncidents,
  findFilteredIncidentIndex,
  getIncidentFilterName,
  sortIncidents,
  syncFilteredIncidentsOnGroupsChange,
} from './utils'

// composables
const {
  hasSameTimeIncidents,
  addIncidentsTimeToIncidentsIdMap,
  updateIncidentsTimeToIncidentsIdMap,
} = useSameTimeIncidentsMap()

// composables
const { incidentsUpdate } = useIncidents()
const { incidentsGroups } = useIncidentsGroup()
const { incidentFiltersToggle } = useIncidentFiltersToggle(hasSameTimeIncidents)
const { incidentFiltersSelect } = useIncidentFiltersSelect()
const { activeIncidentFilters } = useActiveIncidentFilters(
  incidentFiltersToggle,
  incidentFiltersSelect,
)

// state
const filteredIncidents = ref<(Incident | IncidentsGroup)[]>([
  ...incidentsGroups.value,
])
const accordion = ref()

// computed
const isAnyFilterActive = computed(() => !!activeIncidentFilters.value.length)
const isSameTimeFilterActive = computed(() =>
  activeIncidentFilters.value.some((filter) => filter.name === SameTime),
)

// methods
function filterAllIncidents(): void {
  const incidents = filterIncidents(
    incidentsGroups.value,
    activeIncidentFilters.value,
  )

  filteredIncidents.value = isSameTimeFilterActive.value
    ? sortIncidents(incidents)
    : incidents
}

function isIncidentInActiveFilters(
  incident: Incident | IncidentsGroup,
): boolean {
  return filterIncidents([incident], activeIncidentFilters.value).length === 1
}

// init
addIncidentsTimeToIncidentsIdMap(incidentsGroups.value)

watch(
  activeIncidentFilters,
  filterAllIncidents,
) /** every time any filter changes - full filtering */

watchArray(
  incidentsGroups,
  (_newValue, _oldValue, added, removed) => {
    if (
      addIncidentsTimeToIncidentsIdMap(added) &&
      isSameTimeFilterActive.value
    ) {
      filterAllIncidents()
      return
    }

    if (removed || added) {
      filteredIncidents.value = syncFilteredIncidentsOnGroupsChange({
        filteredIncidents: filteredIncidents.value,
        added: added ?? [],
        removed: removed ?? [],
        activeIncidentFilters: activeIncidentFilters.value,
      })
    }
  },
  { deep: true },
)

watch(incidentsUpdate, (newVal) => {
  /**
   * Adjust incidents time updates in map
   */
  if (
    updateIncidentsTimeToIncidentsIdMap(newVal) &&
    isSameTimeFilterActive.value
  ) {
    filterAllIncidents()
    return
  }

  for (const incidentUpdate of newVal) {
    /**
     * Filtering
     *
     * for 'update' and 'delete', either remove or keep modified incident on current filtered view
     */
    if (
      incidentUpdate.data.action === 'update' ||
      incidentUpdate.data.action === 'delete'
    ) {
      // any 'update' with 'same time' filter active will run full filtering
      // not optimal - only changes to occurred_at should trigger full filtering,
      // but current reactivity model doesn't detect changes to occurred_at
      // (because object in reactive incidents array is mutated, not replaced by new array with old objects + new object)
      if (isSameTimeFilterActive.value) {
        filterAllIncidents()
        return
      }

      const incidentIndex = findFilteredIncidentIndex(
        incidentUpdate.data,
        filteredIncidents.value,
      )

      if (incidentIndex === -1) {
        // incident was not visible with previous filters
        // run full filtering (full - because incident can appear at any place in filtered array of incidents)
        filterAllIncidents()

        return
      }

      const filteredIncident = filteredIncidents.value[incidentIndex]

      // incident was included by previous filters, but shouldn't be anymore
      if (!isIncidentInActiveFilters(filteredIncident)) {
        filteredIncidents.value.splice(incidentIndex, 1)
      }
      // else - incident is still visible on currently filtered view, do nothing
    }
  }
})

// expose
defineExpose({
  filteredIncidents,
  isAnyFilterActive,
  isSameTimeFilterActive,
})
</script>

<template>
  <Accordion
    v-model="accordion"
    class="@container/history-filters"
    type="single"
    collapsible
  >
    <AccordionItem value="filters">
      <AccordionHeader>
        <AccordionTrigger class="bg-neutral-light-5 rounded-t-md">
          <div class="flex w-full justify-between">
            <span>Filters</span>
            <span class="pr-1">{{ activeIncidentFilters.length || '' }}</span>
          </div>
        </AccordionTrigger>
        <div
          v-if="activeIncidentFilters.length && !accordion"
          class="bg-neutral-light-5 flex flex-wrap gap-1 px-2 pb-2"
        >
          <FilterTagSelect
            v-for="incidentFilter in incidentFiltersSelect"
            :key="incidentFilter.name"
            :incidentFilter
            @delete="
              Array.isArray(incidentFilter.value)
                ? (incidentFilter.value = [])
                : (incidentFilter.value = undefined)
            "
          />
          <FilterTagToggle
            v-for="incidentFilter in incidentFiltersToggle"
            :key="incidentFilter.name"
            :incidentFilter
            @delete="incidentFilter.value = false"
          />
        </div>
      </AccordionHeader>
      <AccordionContent
        :class="{
          'animate-none!': accordion,
        }"
      >
        <div class="bg-neutral-light-5 space-y-4 p-2 pt-0">
          <IncidentFiltersSelect v-model="incidentFiltersSelect" />

          <!-- calc(50%-5.3rem) : achieve specific width for <slot> (depends on label width of <MultiSelect> inside <IncidentFiltersSelect>)-->
          <div
            class="grid gap-y-3 @[31rem]/history-filters:grid-cols-[1fr_calc(50%-5.3rem)]"
          >
            <div
              class="grid grid-cols-[repeat(auto-fill,minmax(6.25rem,1fr))] gap-y-2"
            >
              <Switch
                v-for="incidentFilter in incidentFiltersToggle"
                :key="incidentFilter.name"
                v-model="incidentFilter.value"
                labelPosition="top"
                :data-testid="`filter-${getIncidentFilterName(incidentFilter).split(' ').join('-')}`"
              >
                <span class="text-nowrap">
                  {{
                    incidentFilter.customName
                      ? incidentFilter.customName
                      : `Show ${incidentFilter.name}`
                  }}
                </span>
              </Switch>
            </div>

            <slot />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>
